// TanStack Query hooks for the App Tasks catalog admin (master only):
// /api/admin/environment-types, /api/admin/task-categories, /api/admin/app-tasks.
// Every mutation also invalidates ["tasks"] — the clip picker's cached catalog — so a rename,
// recolor or (de)activation shows up there without a reload.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type {
  AppTaskCreate,
  AppTaskRead,
  AppTaskUpdate,
  EnvironmentTypeRead,
  TaskCategoryCreate,
  TaskCategoryRead,
  TaskCategoryUpdate,
} from "./types";

const KEYS = {
  environments: ["environment-types"] as const,
  categories: ["task-categories"] as const,
  tasks: ["app-tasks"] as const,
  picker: ["tasks"] as const,
};

export function useEnvironmentTypes() {
  return useQuery({
    queryKey: KEYS.environments,
    queryFn: () => apiFetch<EnvironmentTypeRead[]>("/api/admin/environment-types"),
    staleTime: Infinity,
  });
}

export function useTaskCategories() {
  return useQuery({
    queryKey: KEYS.categories,
    queryFn: () => apiFetch<TaskCategoryRead[]>("/api/admin/task-categories"),
  });
}

export function useAppTasks() {
  return useQuery({
    queryKey: KEYS.tasks,
    queryFn: () => apiFetch<AppTaskRead[]>("/api/admin/app-tasks"),
  });
}

function useInvalidateCatalog() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: KEYS.tasks });
    qc.invalidateQueries({ queryKey: KEYS.categories });
    qc.invalidateQueries({ queryKey: KEYS.picker });
  };
}

export function useCreateAppTask() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (body: AppTaskCreate) =>
      apiFetch<AppTaskRead>("/api/admin/app-tasks", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: invalidate,
  });
}

export function useUpdateAppTask() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AppTaskUpdate }) =>
      apiFetch<AppTaskRead>(`/api/admin/app-tasks/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useCreateTaskCategory() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (body: TaskCategoryCreate) =>
      apiFetch<TaskCategoryRead>("/api/admin/task-categories", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateTaskCategory() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TaskCategoryUpdate }) =>
      apiFetch<TaskCategoryRead>(`/api/admin/task-categories/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteTaskCategory() {
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/admin/task-categories/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
}

/** "R$ 22,00" — the rate is BRL per hour. */
export function formatBrl(v: number | null | undefined): string | null {
  if (v == null) return null;
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

/** Slug from a free-text (EN) name: lowercase, accents stripped, `[a-z0-9_]` only. */
export function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}
